/**
 * Header Component
 * 
 * This component represents the main navigation header of the application.
 * It includes the top bar, logo, search functionality, user account menu,
 * cart and wishlist buttons, and mobile navigation controls.
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Phone, User, ShoppingCart, Heart, LogOut, Menu, X } from 'lucide-react';
import Button from '../Button/Button';
import Cart from '../Cart/Cart';
import Wishlist from '../Wishlist/Wishlist';
import SideNavigation from '../SideNavigation/SideNavigation';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { search_categories } from '../../misc/objs';
import './Header.css';

const Header = ({ sideNavOpen: propSideNavOpen, setSideNavOpen: propSetSideNavOpen }) => {
  // Use passed props if available, otherwise create local state
  const [localSideNavOpen, setLocalSideNavOpen] = useState(false);
  
  // Determine which state and setter to use
  const sideNavOpen = propSideNavOpen !== undefined ? propSideNavOpen : localSideNavOpen;
  const setSideNavOpen = propSetSideNavOpen || setLocalSideNavOpen;
  const [isCartOpen, setIsCartOpen] = useState(false); // Controls cart sidebar visibility
  const [isWishlistOpen, setIsWishlistOpen] = useState(false); // Controls wishlist sidebar visibility
  const [isSearchFocused, setIsSearchFocused] = useState(false); // Tracks if search input is focused
  const [searchQuery, setSearchQuery] = useState(''); // Stores the current search query
  const [selectedCategory, setSelectedCategory] = useState('all'); // Tracks selected search category
  const [userMenuOpen, setUserMenuOpen] = useState(false); // Controls user dropdown menu visibility
  // const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
  const { cartItems } = useCart(); // Access cart items from context
  const { currentUser, logout } = useAuth(); // Access authentication context
  const navigate = useNavigate(); // Router navigation hook

  /**
   * Handles search form submission
   * Navigates to search results page with query parameters
   */
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}&category=${selectedCategory}`);
      setSideNavOpen(false);
    }
  };

  /**
   * Toggles the user dropdown menu visibility
   * Stops event propagation to prevent closing from document click handler
   */
  const toggleUserMenu = (e) => {
    e.stopPropagation();
    setUserMenuOpen(!userMenuOpen);
  };

  /**
   * Handles user sign out process
   * Logs out user, closes menus, and navigates to homepage
   */
  const handleSignOut = async () => {
    try {
      await logout();
      setUserMenuOpen(false);
      setSideNavOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  // Effect to handle body class when side nav is open and handle window resize
  useEffect(() => {
    if (sideNavOpen) {
      document.body.classList.add('side-nav-open');
    } else {
      document.body.classList.remove('side-nav-open');
    }
    
    // Close the navigation drawer when window is resized to desktop
    const handleResize = () => {
      if (window.innerWidth > 768 && sideNavOpen) {
        setSideNavOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.body.classList.remove('side-nav-open');
      window.removeEventListener('resize', handleResize);
    };
  }, [sideNavOpen, setSideNavOpen]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleOutsideClick = () => {
      setUserMenuOpen(false);
    };

    if (userMenuOpen) {
      document.addEventListener('click', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [userMenuOpen]);

  return (
    <header className={`header ${sideNavOpen ? 'side-nav-open' : ''}`}>
      {/* Top bar with links and contact info */}
      <div className="top-bar">
        <div className="container top-bar-content">
          <div className="top-bar-left">
            <Link to="/about" className="top-bar-link">About Us</Link>
            <Link to="/contacts" className="top-bar-link">Contacts</Link>
            {/* <Link to="/store-locator" className="top-bar-link">Store Location</Link>
            <Link to="/track-order" className="top-bar-link">Track Order</Link>
            <Link to="/blog" className="top-bar-link">Blog</Link> */}
          </div>
          <div className="top-bar-right">
            <div className="contact-info">
              <Phone size={16} />
              <span>(424)249-5973</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main header with logo, search, and action buttons */}
      <div className="main-header">
        <div className="container main-header-content">
          {/* Mobile menu toggle button */}
          <button 
            className="hamburger-menu-toggle" 
            onClick={() => setSideNavOpen(!sideNavOpen)}
            aria-label="Toggle menu"
          >
            {sideNavOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          {/* Site logo */}
          <Link to="/" className="logo">
            <img 
              src="/images/company-logo.png" 
              alt="SNAGITCHEAP.COM" 
              className="logo-image"
            />
          </Link>

          {/* Search section with category dropdown and search input */}
          <div className={`search-section ${isSearchFocused ? 'focused' : ''}`}>
            <form onSubmit={handleSearch} className="search-container">
              <div className="category-select">
                <select 
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {search_categories.map(category => (
                    <option key={category} value={category.toLowerCase().replace(' ', '-')}>
                       {category}
                    </option>
                  ))}
                </select>
              </div>
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="Search over 10,000 products"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                />
                <Button 
                  type="submit"
                  className="search-button"
                >
                  <Search size={20} />
                </Button>
              </div>
            </form>
          </div>

          {/* Header action buttons (wishlist, cart, user account) */}
          <div className="header-actions">
            {/* Wishlist button */}
            <button 
              className="wishlist-button"
              onClick={() => setIsWishlistOpen(true)}
              title="Wishlist"
            >
              <Heart size={24} />
            </button>
            {/* Cart button with item count indicator */}
            <button
              className="cart-button"
              onClick={() => setIsCartOpen(true)}
              title="Cart"
            >
              <ShoppingCart size={24} />
              {cartItems.length > 0 && (
                <span className="cart-count">{cartItems.length}</span>
              )}
            </button>
            {/* User account menu or login button */}
            {currentUser ? (
              <div className="user-menu" onClick={(e) => e.stopPropagation()}>
                <button 
                  className={`user-button ${userMenuOpen ? 'active' : ''}`}
                  onClick={toggleUserMenu}
                >
                  <User size={24} />
                  <span className="user-name">Account</span>
                </button>
                <div className={`user-dropdown ${userMenuOpen ? 'open' : ''}`}>
                  <Link to="/profile" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>Profile</Link>
                  <Link to="/orders" className="dropdown-item" onClick={() => setUserMenuOpen(false)}>Orders</Link>
                  <button onClick={handleSignOut} className="dropdown-item sign-out-button">
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <Link to="/login" className="login-button">
                <User size={24} />
                <span>Sign In</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Side Navigation Component */}
      <SideNavigation 
        isOpen={sideNavOpen}
        onClose={() => setSideNavOpen(false)}
        onOpenWishlist={() => setIsWishlistOpen(true)}
        onOpenCart={() => setIsCartOpen(true)}
      />

      {/* Cart and wishlist sidebars */}
      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      <Wishlist isOpen={isWishlistOpen} onClose={() => setIsWishlistOpen(false)} />
    </header>
  );
};

export default Header;